import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, ExternalLink, Coins as CoinsIcon } from 'lucide-react';
import CommentSection from '../CommentSection';
import CopyButton from '../CopyButton';
import TokenHolders from './TokenHolders';
import SafetyLevel from './SafetyLevel';
import WatchButton from './WatchButton';
import { normalizeChain, getChain } from '../chainUtils';
import { getSecurityLevelInfo } from '../utils/securityLevelUtils';
import { getWatchedItems, addWatchedCoin, removeWatchedCoin, addWatchedAddress, isWatchingCoin, MAX_WATCHED_ITEMS } from '../userService';

const CoinInfo = ({ coin, coinId, user, onLogin, holdersData }) => {
  const [isWatching, setIsWatching] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);
  const [watchedItems, setWatchedItems] = useState([]);
  const [watchError, setWatchError] = useState('');
  const [showWatchOptions, setShowWatchOptions] = useState(false);

  // Compute unique eligible EVM addresses from platforms
  // Only count addresses from supported chains, deduplicated at address level
  const eligibleAddresses = React.useMemo(() => {
    if (!coin.platforms) return [];
    const addressSet = new Set();
    Object.entries(coin.platforms).forEach(([, chainValue]) => {
      if (chainValue.isSupportedChain && chainValue.address) {
        // Normalize to lowercase for deduplication
        addressSet.add(chainValue.address.toLowerCase());
      }
    });
    return Array.from(addressSet);
  }, [coin.platforms]);

  // Load watched items to check if this coin is being watched
  useEffect(() => {
    if (!user) {
      setIsWatching(false);
      setWatchedItems([]);
      return;
    }

    const loadWatchedItems = async () => {
      try {
        const items = await getWatchedItems();
        setWatchedItems(items);
        setIsWatching(isWatchingCoin(coinId, items));
      } catch (error) {
        console.error('Error loading watched items:', error);
      }
    };

    loadWatchedItems();
  }, [user, coinId]);

  // Handle watch/unwatch toggle
  const handleWatchToggle = async () => {
    if (!user) {
      onLogin?.();
      return;
    }

    // If unwatching, proceed directly
    if (isWatching) {
      setWatchLoading(true);
      setWatchError('');
      try {
        await removeWatchedCoin(coinId);
        setIsWatching(false);
        setWatchedItems(prev => prev.filter(item => !(item.type === 'coin' && item.coinId === coinId)));
      } catch (error) {
        console.error('Error unwatching:', error);
        setWatchError('Failed to unwatch');
        setTimeout(() => setWatchError(''), 5000);
      } finally {
        setWatchLoading(false);
      }
      return;
    }

    // If watching and has eligible addresses, show options dialog
    if (eligibleAddresses.length > 0) {
      setShowWatchOptions(true);
      return;
    }

    // No eligible addresses, watch coin only directly
    await handleWatchConfirm(false);
  };

  // Handle watch confirmation (coin only or coin + addresses)
  const handleWatchConfirm = async (includeAddresses) => {
    setShowWatchOptions(false);
    setWatchLoading(true);
    setWatchError('');

    // Calculate how many items will be added
    const itemsToAdd = includeAddresses ? 1 + eligibleAddresses.length : 1;

    // Check limit
    if (watchedItems.length + itemsToAdd > MAX_WATCHED_ITEMS) {
      const available = MAX_WATCHED_ITEMS - watchedItems.length;
      setWatchError(
        `Cannot add ${itemsToAdd} items. Only ${available} slot${available !== 1 ? 's' : ''} available. Go to Settings → Watching to manage.`
      );
      setTimeout(() => setWatchError(''), 5000);
      setWatchLoading(false);
      return;
    }

    try {
      // Add the coin first
      const coinResult = await addWatchedCoin(coinId, coin.name, coin.symbol, coin.image);
      if (!coinResult.success) {
        setWatchError(coinResult.error || 'Failed to watch coin');
        setTimeout(() => setWatchError(''), 5000);
        setWatchLoading(false);
        return;
      }

      setIsWatching(true);
      const newItems = [{ type: 'coin', coinId, coinName: coin.name, coinSymbol: coin.symbol }];

      // If including addresses, add them too
      if (includeAddresses && eligibleAddresses.length > 0) {
        const addressResults = await Promise.all(
          eligibleAddresses.map(addr => addWatchedAddress(addr))
        );

        // Check for any failures
        const failures = addressResults.filter(r => !r.success);
        if (failures.length > 0) {
          console.warn('Some addresses failed to add:', failures);
          // Still proceed - coin was added successfully
        }

        // Add successfully added addresses to local state
        addressResults.forEach((result, idx) => {
          if (result.success) {
            newItems.push({ type: 'address', targetAddress: eligibleAddresses[idx] });
          }
        });
      }

      setWatchedItems(prev => [...prev, ...newItems]);
    } catch (error) {
      console.error('Error watching:', error);
      setWatchError('Failed to watch');
      setTimeout(() => setWatchError(''), 5000);
    } finally {
      setWatchLoading(false);
    }
  };
  // Get chain info safely (returns null for unsupported chains)
  const getChainInfo = (platformName) => {
    try {
      const normalizedChain = normalizeChain(platformName);
      const chainConfig = getChain(normalizedChain);
      return chainConfig ? { icon: chainConfig.icon, name: chainConfig.name } : null;
    } catch {
      return null;
    }
  };

  const formatLargeNumber = (num) => {
    if (!num) return 'N/A';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };

  const priceChange = coin.price_change_percentage_24h;
  const isPriceUp = priceChange >= 0;

  return (
    <div className="space-y-6">
      {/* Coin Information */}
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <CoinsIcon className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <h3 className="text-xl font-bold text-gray-800">Coin Information</h3>
          </div>
        </div>

        {/* Coin Header */}
        <div className="flex justify-center mb-6">
          <div className={`flex ${coin.image ? 'items-center gap-4' : 'flex-col items-center'}`}>
            {coin.image && (
              <img
                src={coin.image}
                alt={coin.name}
                className="w-16 h-16 rounded-full flex-shrink-0"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <div className={coin.image ? '' : 'text-center'}>
              <div className={`flex items-center gap-3 flex-wrap mb-2 ${coin.image ? '' : 'justify-center'}`}>
                <h2 className="text-2xl font-bold text-gray-900 break-words max-w-full">{coin.name}</h2>
                <span className="text-lg text-gray-500 uppercase font-mono break-all max-w-full">
                  {coin.symbol}
                </span>
                {coin.market_cap_rank && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold whitespace-nowrap">
                    Rank #{coin.market_cap_rank}
                  </span>
                )}
                {/* Watch Button */}
                <WatchButton
                  isWatching={isWatching}
                  loading={watchLoading}
                  onClick={handleWatchToggle}
                  error={watchError}
                />
              </div>

              {/* Price & Change */}
              <div className={`flex items-baseline gap-3 flex-wrap ${coin.image ? '' : 'justify-center'}`}>
                <div className="text-3xl font-bold text-gray-900 break-all">
                  {coin.current_price ? `$${coin.current_price.toLocaleString()}` : 'N/A'}
                </div>
                {priceChange !== null && priceChange !== undefined && (
                  <div className={`flex items-center gap-1 font-semibold whitespace-nowrap ${isPriceUp ? 'text-green-600' : 'text-red-600'}`}>
                    {isPriceUp ? <TrendingUp className="w-4 h-4 flex-shrink-0" /> : <TrendingDown className="w-4 h-4 flex-shrink-0" />}
                    <span>
                      {isPriceUp ? '+' : ''}{priceChange.toFixed(2)}%
                    </span>
                    <span className="text-xs text-gray-500 font-normal ml-1">(24h)</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Safety Level */}
        <SafetyLevel level={coin.securityLevel} description={coin.securityDescription} centered />

        {/* Holders Section - Lazy Loaded */}
        {coin.platforms && Object.keys(coin.platforms).length > 0 && holdersData?.totalHolders != null && (
          <TokenHolders
            totalHolders={holdersData.totalHolders}
            holdersByAcquisition={holdersData.holdersByAcquisition}
            platformCount={holdersData.platformCount}
            totalPlatforms={Object.keys(coin.platforms).length}
          />
        )}

        {/* Market Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b border-gray-200">
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1">Market Cap</p>
            <p className="text-base font-semibold text-gray-900">
              {formatLargeNumber(coin.market_cap)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1">24h Volume</p>
            <p className="text-base font-semibold text-gray-900">
              {formatLargeNumber(coin.total_volume)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1">Circulating Supply</p>
            <p className="text-base font-semibold text-gray-900">
              {coin.circulating_supply ? coin.circulating_supply.toLocaleString(undefined, { maximumFractionDigits: 0 }) : 'N/A'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1">Max Supply</p>
            <p className="text-base font-semibold text-gray-900">
              {coin.max_supply ? coin.max_supply.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '∞'}
            </p>
          </div>
        </div>

        {/* 24h High/Low */}
        {(coin.high_24h || coin.low_24h) && (
          <div className="mb-6 pb-6 border-b border-gray-200">
            <p className="text-xs text-gray-600 mb-2 text-center">24h Price Range</p>
            <div className="flex items-center gap-4 justify-center">
              <div className="flex-1 max-w-md">
                <div className="h-2 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full"></div>
                <div className="flex justify-between mt-2 text-xs">
                  <span className="text-gray-900 font-semibold">
                    ${coin.low_24h?.toLocaleString() || 'N/A'}
                  </span>
                  <span className="text-gray-900 font-semibold">
                    ${coin.high_24h?.toLocaleString() || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contract Addresses */}
        {coin.platforms && Object.keys(coin.platforms).length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3 text-center">Contract Addresses</h4>
            <div className="flex justify-center px-2">
              <div className="space-y-2 w-full max-w-2xl">
                {Object.entries(coin.platforms).map(([chainName, chainValue]) => {
                  const platformSecurityLevel = coin.platformSecurityLevels?.[chainName.toLowerCase()];
                  const securityInfo = getSecurityLevelInfo(platformSecurityLevel);
                  return (
                  <div
                    key={chainName}
                    className="flex items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      {(() => {
                        const chainInfo = getChainInfo(chainName);
                        return (
                          <div
                            className="text-xs text-gray-500 mb-0.5 flex items-center gap-1 cursor-default"
                            title={chainInfo?.name || chainName}
                          >
                            {chainInfo?.icon && <span>{chainInfo.icon}</span>}
                            <span>{chainName}</span>
                          </div>
                        );
                      })()}
                      <code className="text-xs font-mono text-gray-700 truncate block">
                        {chainValue.address}
                      </code>
                      {platformSecurityLevel != null && (
                        <div className="mt-1 text-xs">
                          <span className="text-gray-600">Safety Level: </span>
                          <span className={`px-1.5 py-0.5 rounded font-medium ${securityInfo.bg} ${securityInfo.textColor}`}>
                            {securityInfo.text}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <CopyButton text={chainValue.address} label="address" />
                      {chainValue.isSupportedChain && (
                        <Link
                          to={`/chain/${chainName}/${chainValue.address}`}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium whitespace-nowrap"
                        >
                          View
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Data Attribution */}
        {coin.last_updated && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Powered by{' '}
              <a
                href={`https://www.coingecko.com/en/coins/${coinId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 hover:underline"
              >
                CoinGecko
              </a>
              . Data last updated at {new Date(coin.last_updated.toMillis()).toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {/* Community Insights */}
      <CommentSection
        targetType="coin"
        targetCoinId={coinId}
        coinName={coin.name}
        coinPlatforms={coin.platforms}
        user={user}
        onLogin={onLogin}
      />

      {/* Watch Options Dialog */}
      {showWatchOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Watch {coin.name}</h3>
            <p className="text-sm text-gray-600 mb-4">
              This coin has {eligibleAddresses.length} contract address{eligibleAddresses.length !== 1 ? 'es' : ''} on supported chains.
              Would you like to watch the coin only, or the coin plus its contract addresses?
            </p>

            <div className="space-y-3 mb-4">
              {(() => {
                const availableSlots = MAX_WATCHED_ITEMS - watchedItems.length;
                const coinPlusAddressCount = 1 + eligibleAddresses.length;
                const canAddCoinOnly = availableSlots >= 1;
                const canAddCoinPlusAddresses = availableSlots >= coinPlusAddressCount;

                return (
                  <>
                    <button
                      onClick={() => handleWatchConfirm(false)}
                      disabled={!canAddCoinOnly}
                      className={`w-full flex items-center justify-between p-3 border rounded-lg transition ${
                        canAddCoinOnly
                          ? 'border-gray-200 hover:bg-gray-50'
                          : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="text-left">
                        <div className="font-medium text-gray-900">Coin only</div>
                        <div className="text-xs text-gray-500">Watch {coin.symbol} price and comments</div>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">1 item</span>
                    </button>

                    <button
                      onClick={() => handleWatchConfirm(true)}
                      disabled={!canAddCoinPlusAddresses}
                      className={`w-full flex items-center justify-between p-3 border rounded-lg transition ${
                        canAddCoinPlusAddresses
                          ? 'border-blue-200 bg-blue-50 hover:bg-blue-100'
                          : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="text-left">
                        <div className="font-medium text-gray-900">Coin + addresses</div>
                        <div className="text-xs text-gray-500">
                          {canAddCoinPlusAddresses
                            ? 'Also watch contract address activity'
                            : `Requires ${coinPlusAddressCount} slots (only ${availableSlots} available)`}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        canAddCoinPlusAddresses ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {coinPlusAddressCount} items
                      </span>
                    </button>
                  </>
                );
              })()}
            </div>

            <div className="text-xs text-gray-500 mb-4">
              You have {MAX_WATCHED_ITEMS - watchedItems.length} of {MAX_WATCHED_ITEMS} watch slot(s) available.
            </div>

            <button
              onClick={() => setShowWatchOptions(false)}
              className="w-full py-2 text-sm text-gray-600 hover:text-gray-800 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoinInfo;
